import type { ElementType, HTMLAttributes, ReactNode } from "react";

import { cn } from "@src/shared/lib/utils";

export interface HeaderTextProps extends HTMLAttributes<HTMLElement> {
  tag?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  fontWeight?: 400 | 500 | 600 | 700;
  children?: ReactNode;
}

const sizeByTag: Record<NonNullable<HeaderTextProps["tag"]>, string> = {
  h1: "text-3xl tracking-tight",
  h2: "text-2xl tracking-tight",
  h3: "text-xl",
  h4: "text-lg",
  h5: "text-base",
  h6: "text-sm uppercase tracking-wide",
};

const weightClass: Record<
  NonNullable<HeaderTextProps["fontWeight"]>,
  string
> = {
  400: "font-normal",
  500: "font-medium",
  600: "font-semibold",
  700: "font-bold",
};

/**
 * Semantic header text with Stitch brand styling. Replaces the legacy vital
 * HeaderText wrapper.
 */
export const HeaderText = ({
  tag = "h2",
  fontWeight = 600,
  className,
  children,
  ...rest
}: HeaderTextProps) => {
  const Component = tag as ElementType;

  return (
    <Component
      className={cn(
        "text-foreground",
        sizeByTag[tag],
        weightClass[fontWeight],
        className,
      )}
      {...rest}
    >
      {children}
    </Component>
  );
};
